import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LifeBuoy, ChevronRight } from "lucide-react";

export default function HelpCenter() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = async (query) => { const { data } = await api.get(`/help/search${query ? `?q=${encodeURIComponent(query)}` : ""}`); setItems(data.items || []); };
  useEffect(() => { load(""); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q]);

  const cats = [...new Set(items.map((i) => i.category))];

  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Help Center" subtitle="Guidance based on how this CRM actually works — forms, Dropbox, email, conflicts and more." />
      <Input data-testid="help-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help…" className="h-10 max-w-md bg-white text-sm mb-5" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 space-y-4">
          {cats.map((c) => (
            <div key={c}>
              <div className="text-[11px] uppercase tracking-wide font-semibold text-mutedsea mb-1">{c}</div>
              <div className="space-y-1">
                {items.filter((i) => i.category === c).map((a) => (
                  <button key={a.id} data-testid={`help-article-${a.id}`} onClick={() => setActive(a)}
                    className={`w-full text-left px-3 py-2 rounded-sm text-sm flex items-center justify-between nav-link ${active?.id === a.id ? "bg-navy text-white" : "bg-white text-slate hover:bg-ice border border-border"}`}>
                    {a.title} <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2">
          {active ? <ArticleView a={active} /> : (
            <div className="bg-white border border-border rounded-sm p-10 text-center text-mutedsea"><LifeBuoy className="h-8 w-8 mx-auto mb-2 text-sky" /> Select an article to read.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ArticleView({ a }) {
  return (
    <div className="bg-white border border-border rounded-sm p-6" data-testid="help-article-view">
      <div className="flex items-center gap-2 mb-1"><Badge variant="outline" className="bg-ice text-atlantic border-sky/50">{a.category}</Badge><span className="text-[11px] text-mutedsea">v{a.version} · updated {(a.last_updated || "").slice(0, 10)}</span></div>
      <h2 className="font-serif text-2xl text-navy mb-2">{a.title}</h2>
      <p className="text-sm text-slate mb-3">{a.summary}</p>
      {a.purpose && <p className="text-sm text-mutedsea mb-1"><b className="text-slate">Purpose:</b> {a.purpose}</p>}
      {a.when_to_use && <p className="text-sm text-mutedsea mb-3"><b className="text-slate">When to use:</b> {a.when_to_use}</p>}
      {a.steps?.length > 0 && <><div className="text-xs font-semibold text-slate mt-3 mb-1">Steps</div><ol className="list-decimal ml-5 text-sm text-slate space-y-1">{a.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></>}
      {a.rules?.length > 0 && <><div className="text-xs font-semibold text-slate mt-3 mb-1">Important rules</div><ul className="list-disc ml-5 text-sm text-slate space-y-1">{a.rules.map((s, i) => <li key={i}>{s}</li>)}</ul></>}
      {a.related_modules?.length > 0 && <div className="text-xs text-mutedsea mt-4">Related: {a.related_modules.join(", ")}</div>}
    </div>
  );
}
